import * as THREE from 'three'
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'

import EventEmitter from './EventEmitter'
import type { Source } from '../sources'

export type LoadedResource = THREE.Texture | THREE.CubeTexture | GLTF

interface Loaders {
  gltf: GLTFLoader
  draco: DRACOLoader
  texture: THREE.TextureLoader
  cubeTexture: THREE.CubeTextureLoader
}

/**
 * Loads every `Source` through a shared LoadingManager and emits:
 *  - `progress` (loaded, total) on each asset
 *  - `ready` once everything is in `items`
 */
export default class Resources extends EventEmitter {
  public readonly sources: Source[]
  public readonly items: Record<string, LoadedResource> = {}
  public readonly toLoad: number
  public loaded = 0

  private loaders!: Loaders

  constructor(sources: Source[]) {
    super()
    this.sources = sources
    this.toLoad = sources.length

    this.setLoaders()
    this.startLoading()
  }

  private setLoaders(): void {
    const draco = new DRACOLoader()
    // Google-hosted decoder; swap for a self-hosted path in /public for offline use.
    draco.setDecoderPath(
      'https://www.gstatic.com/draco/versioned/decoders/1.5.7/',
    )

    const gltf = new GLTFLoader()
    gltf.setDRACOLoader(draco)

    this.loaders = {
      gltf,
      draco,
      texture: new THREE.TextureLoader(),
      cubeTexture: new THREE.CubeTextureLoader(),
    }
  }

  private startLoading(): void {
    if (this.toLoad === 0) {
      // Nothing to load — signal ready on the next frame so listeners attach first.
      window.requestAnimationFrame(() => this.trigger('ready'))
      return
    }

    for (const source of this.sources) {
      switch (source.type) {
        case 'gltfModel':
          this.loaders.gltf.load(source.path as string, (file) =>
            this.onLoaded(source, file),
          )
          break
        case 'texture':
          this.loaders.texture.load(source.path as string, (file) =>
            this.onLoaded(source, file),
          )
          break
        case 'cubeTexture':
          this.loaders.cubeTexture.load(source.path as string[], (file) =>
            this.onLoaded(source, file),
          )
          break
      }
    }
  }

  private onLoaded(source: Source, file: LoadedResource): void {
    this.items[source.name] = file
    this.loaded += 1
    this.trigger('progress', this.loaded, this.toLoad)

    if (this.loaded === this.toLoad) {
      this.trigger('ready')
    }
  }
}
